import { h, defineComponent, ref, computed } from 'vue';
import { Modal, modalProps, ModalProps } from '@/ui/components/Modal';
import { Button } from '@/ui/components/Button';
import { pageSubtitle } from '@/ui/components';
import { numberInput } from '@/ui/components/form/NumberInput';
import { fetchUser, findQuota, user } from '@/ui/auth/auth';
import { toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';
import { Icon } from '@/ui/components/icon/Icon';
import axios from 'axios';
import { updateSubscription } from '@/services/api/resources/chargebee/subscription/actions';

export const AddUserSeatsModal = defineComponent<ModalProps>({
  name: 'AddUserModal',
  props: modalProps,
  setup(props, context) {
    const currentUsedUsersSeats = findQuota('user')!.current;

    const numberSeats = ref(0);

    const closeModal = () => {
      context.emit('close');
      numberSeats.value = 0;
    };

    const totalNumberSeats = computed(() => numberSeats.value + currentUsedUsersSeats);

    const updatingSubscription = ref(false);

    const updateUserSubscription = async () => {
      updatingSubscription.value = true;

      // We are using legacy here since we will be refactoring the subscription.
      // When refactor the subscription logic we can refactor this part
      try {
        await updateSubscription(user.value.subscription!.id, totalNumberSeats.value)();
        toasted.success(t('add-user-modal.seats-added', { numberUserSeats: numberSeats.value.toString() }));
        await fetchUser()();
        context.emit('created:user-seat');
      } catch (e) {
        if (axios.isAxiosError(e)) {
          toasted.error(e.response?.data.message);
        } else {
          toasted.error(t('common.error'));
          console.error(e);
        }
      }

      updatingSubscription.value = false;
    };

    return function render() {
      const addUserModalContent = () => [
        pageSubtitle(t('add-user-modal.title')),
        h('div', t('add-user-modal.subtitle')),
        h('div', { class: 'add-user-modal__input-row' }, [
          h('div', { class: 'has-text-weight-semibold' }, t('add-user-modal.add-more-seats')),
          numberInput({
            value: numberSeats.value,
            min: 1,
            controlsPosition: 'compact',
            size: 'is-small',
            controlsRounded: true,
          }, v => {
            numberSeats.value = v;
          }),
        ]),
        h('div', { class: 'add-user-modal__results-row' }, [
          h('div', t('add-user-modal.total-user-seats')),
          h('div', totalNumberSeats.value.toString()),
        ]),
        h('div', { class: 'add-user-modal__info-row' }, [
          h(Icon, {
            props: {
              iconPack: 'fal',
              icon: 'info-circle',
              size: '1x',
            },
          }),
          t('add-user-modal.info'),
        ]),
      ];

      return h(Modal, {
        props: {
          ...props,
          customClass: 'add-user-modal',
          width: '465px',
          ariaRole: 'dialog',
          trapFocus: true,
          destroyOnHide: false,
          ariaModal: true,
        },
        scopedSlots: {
          actions: () => h('div', [
            h(Button, {
              props: { type: 'is-text', btnClass: 'mr-2 has-text-weight-semibold' },
              on: { click: closeModal },
            }, t('modal.cancel')),
            h(Button, {
              props: {
                loading: updatingSubscription.value,
                disabled: numberSeats.value < 1,
              },
              on: {
                click: updateUserSubscription,
              },
            }, t('add-user-modal.add-users-seats')),
          ]),
        },
        on: {
          close: closeModal,
        },
      }, addUserModalContent());
    };
  },
});
